// A brand's design tokens: the small semantic set a buyer may change (spec/03 §12 "Design tokens",
// decided by the maintainer in #463).
//
// **Not a token override.** The design tokens (`packages/config/tokens/clavian.tokens.json`) are
// forty-odd colours, scales and stacks tuned together and checked pairwise for AA on every build. A
// buyer who could set any of them could ship an unreadable kiosk. So a buyer chooses six things:
//
//   colour.primary   fills: primary actions, the mark's keyhole, the time ring, large display
//                    numbers. Against the surface it needs **3:1** (large text and interface parts).
//   colour.accent    text and links on the surface, and the focus ring. It needs **4.5:1** (text).
//   surface          `dark` or `light`: which of the two neutral sets the brand's surfaces stand on.
//   radius           `sharp`, `soft` (Clavian's) or `round`, mapped onto the existing radius scale.
//   font.display     headings and display numbers, from the curated list (`fonts.ts`).
//   font.body        body, interface and labels, from the same list.
//
// Everything is optional, and **what a buyer does not supply is the Clavian default**, untouched.
// What the platform derives — the hover and pressed tones, the text on a primary fill, the text tone
// for the other light/dark mode — it computes (`@clavian/domain`, `brandTokenOverrides`); nothing
// derived is ever supplied, so nothing derived can fail AA.
//
// **A colour below AA is refused**, with a validation error naming the pair: the colour, the ground
// it failed on, the ratio and the ratio it needs. It is checked against every ground of the chosen
// surface (`bg`, `surface`, `surface-2`), because a button sits on a card as often as on the page.
import { z } from "../zod.js";
import { contrastRatio, formatRatio, HEX_COLOUR_PATTERN, HexColourSchema, WCAG_AA_LARGE_AND_UI, WCAG_AA_TEXT } from "./colour.js";
import { BRAND_BODY_FONT_IDS, BRAND_DISPLAY_FONT_IDS } from "./fonts.js";
export const BRAND_SURFACES = ['dark', 'light'];
export const BRAND_RADII = ['sharp', 'soft', 'round'];
/** Clavian is dark-first (spec/BRAND.md §4): a brand that names no surface stands on the dark one. */
export const DEFAULT_BRAND_SURFACE = 'dark';
/** Clavian's own corners. */
export const DEFAULT_BRAND_RADIUS = 'soft';
/**
 * The grounds a colour is painted on, per surface: the design tokens' `bg`, `surface` and
 * `surface-2` in each mode (`packages/config/tokens/clavian.tokens.json`, `color.mode`). Written out
 * because this package cannot import `@clavian/config` at run time; `src/__tests__/brand.test.ts`
 * fails the build the moment the two disagree.
 */
export const SURFACE_GROUNDS = {
    dark: { bg: '#030409', surface: '#080910', 'surface-2': '#0D0E18' },
    light: { bg: '#F7F5F2', surface: '#FFFFFF', 'surface-2': '#F3F0EC' },
};
export const BrandSurfaceSchema = z.enum(BRAND_SURFACES).openapi('BrandSurface', {
    description: 'The ground a brand’s surfaces stand on: the dark or the light neutral set.',
});
export const BrandRadiusSchema = z.enum(BRAND_RADII).openapi('BrandRadius', {
    description: 'Corner style, mapped onto the radius scale: `sharp`, `soft` (Clavian’s) or `round`.',
});
/**
 * A font id, checked against the list by refinement rather than typed as an enum: the list grows,
 * and a family added to it is then no change to the contract at all (an enum value added to a
 * response is a breaking change for the generated clients).
 */
function fontIdSchema(ids, role) {
    return z
        .string()
        .refine((id) => ids.includes(id), { message: `Expected a ${role} font from the brand list: ${ids.join(', ')}` })
        .openapi({ description: `A ${role} font from the brand list (spec/03 §12): ${ids.join(', ')}.` });
}
export const BrandDisplayFontSchema = fontIdSchema(BRAND_DISPLAY_FONT_IDS, 'display');
export const BrandBodyFontSchema = fontIdSchema(BRAND_BODY_FONT_IDS, 'body');
export const BrandColoursSchema = z
    .object({
    /** Fills: primary actions, the keyhole, the time ring. 3:1 against the surface. */
    primary: HexColourSchema.optional(),
    /** Text and links on the surface, and the focus ring. 4.5:1 against the surface. */
    accent: HexColourSchema.optional(),
})
    .strict()
    .openapi('BrandColours', { description: 'The primary (fills) and accent (text and links) colours. Either may be left to the default.' });
export const BrandFontsSchema = z
    .object({ display: BrandDisplayFontSchema.optional(), body: BrandBodyFontSchema.optional() })
    .strict()
    .openapi('BrandFonts', { description: 'The heading and body faces, by id from the brand list.' });
const ROLE = {
    primary: { bar: WCAG_AA_LARGE_AND_UI, needs: 'buttons, marks and large text need 3:1' },
    accent: { bar: WCAG_AA_TEXT, needs: 'text and links need 4.5:1' },
};
/**
 * Every supplied colour that fails WCAG 2.2 AA against the surface it will stand on, each with the
 * worst of the surface's grounds named. Empty when both pass or neither is supplied. The server
 * runs the same function over an organisation's colours against the Operator's surface
 * (`org.branding`), so an organisation and the Operator are refused in the same words.
 */
export function brandColourProblems(colours, surface = DEFAULT_BRAND_SURFACE) {
    const problems = [];
    // A refinement runs even when a field has already failed its own check, so a value that is not a
    // colour, or a surface that is not one, is left to the error that field already carries.
    if (!BRAND_SURFACES.includes(surface))
        return problems;
    for (const key of ['primary', 'accent']) {
        const colour = colours?.[key];
        if (colour === undefined || !HEX_COLOUR_PATTERN.test(colour))
            continue;
        let worst;
        for (const [ground, hex] of Object.entries(SURFACE_GROUNDS[surface])) {
            const ratio = contrastRatio(colour, hex);
            if (worst === undefined || ratio < worst.ratio)
                worst = { ground, hex, ratio };
        }
        if (worst !== undefined && worst.ratio < ROLE[key].bar) {
            problems.push({
                path: ['colour', key],
                message: `colour.${key} ${colour.toUpperCase()} on the ${surface} surface's ${worst.ground} ${worst.hex} is ` +
                    `${formatRatio(worst.ratio)}; ${ROLE[key].needs} (WCAG 2.2 AA).`,
            });
        }
    }
    return problems;
}
export const BrandThemeSchema = z
    .object({
    colour: BrandColoursSchema.optional(),
    surface: BrandSurfaceSchema.optional(),
    radius: BrandRadiusSchema.optional(),
    font: BrandFontsSchema.optional(),
})
    .strict()
    .superRefine((theme, context) => {
    for (const problem of brandColourProblems(theme.colour, theme.surface)) {
        context.addIssue({ code: 'custom', path: [...problem.path], message: problem.message });
    }
})
    .openapi('BrandTheme', {
    description: 'A brand’s design tokens: primary and accent colours, a dark or light surface, a corner style and two ' +
        'fonts from the list. What is left out is the Clavian default. A colour below WCAG AA on the surface is refused.',
});
//# sourceMappingURL=theme.js.map